import React from "react";
import { Link } from "react-router-dom";
import { StaticPage, Section, Bullets } from "../components/StaticPage";

export const TermsOfService = () => (
  <StaticPage
    title="Terms of Service"
    subtitle="Welcome to Absolute Cinema."
    lastUpdated="September 5, 2026"
    testId="terms-of-service-page"
  >
    <p>These Terms of Service govern your access to and use of the Absolute Cinema website.</p>
    <p>By accessing or using the website, you agree to comply with these Terms.</p>

    <Section heading="1. Use of the Website">
      <p>
        Absolute Cinema provides cinema-related entertainment, information, interactive features,
        games, audio features, and other website functionality.
      </p>
      <p>You may use the website for lawful purposes and in accordance with these Terms.</p>
    </Section>

    <Section heading="2. Acceptable Use">
      <p>You agree not to:</p>
      <Bullets items={[
        "Use the website for unlawful purposes",
        "Attempt to gain unauthorized access to the website or its systems",
        "Interfere with the operation or security of the website",
        "Introduce malicious software, code, or harmful material",
        "Attempt to disrupt or damage the website",
        "Abuse or misuse website features",
        "Attempt to circumvent security or access controls",
        "Use the website in a manner that violates applicable laws or regulations",
      ]} />
    </Section>

    <Section heading="3. Website Content">
      <p>
        Absolute Cinema may contain text, graphics, interactive features, games, audio, links,
        references, and other materials.
      </p>
      <p>
        We make reasonable efforts to maintain the website and provide a useful experience, but we
        do not guarantee that all information will always be complete, current, accurate, or
        error-free.
      </p>
    </Section>

    <Section heading="4. Intellectual Property">
      <p>Absolute Cinema respects intellectual-property rights.</p>
      <p>
        Third-party movies, movie-related materials, audio, music, recordings, images, artwork,
        characters, logos, trademarks, screenshots, titles, and other third-party intellectual
        property belong to their respective owners.
      </p>
      <p>
        Absolute Cinema does not claim ownership of third-party copyrighted material or intellectual
        property.
      </p>
      <p>Nothing in these Terms transfers ownership of third-party intellectual property to Absolute Cinema.</p>
      <p>
        The appearance or reference of third-party names, titles, trademarks, artwork, audio, music,
        movies, characters, or other materials on the website does not necessarily indicate
        sponsorship, endorsement, authorization, or official affiliation with the relevant rights
        holder.
      </p>
    </Section>

    <Section heading="5. Audio and Media">
      <p>Absolute Cinema may provide audio-related or other media-related features for entertainment purposes.</p>
      <p>
        Any third-party audio, music, recordings, movie-related audio, or other third-party media
        belongs to its respective copyright owner or rights holder.
      </p>
      <p>
        Absolute Cinema does not claim ownership of third-party audio, music, recordings, or other
        copyrighted media.
      </p>
      <p>
        Where third-party material is referenced, displayed, embedded, linked, or otherwise made
        available through the website, ownership of that material remains with the applicable rights
        holder.
      </p>
      <p>
        If you are a rights holder and have a legitimate copyright or intellectual-property concern
        regarding material associated with Absolute Cinema, please contact us through our{" "}
        <Link to="/contact" className="ac-gold-text hover:underline">Contact Us</Link> page.
      </p>
    </Section>

    <Section heading="6. Games and Interactive Features">
      <p>Absolute Cinema may provide games and other interactive features for entertainment purposes.</p>
      <p>
        Where third-party names, trademarks, artwork, characters, game assets, or other intellectual
        property are used or referenced, such intellectual property remains the property of its
        respective owners.
      </p>
      <p>Absolute Cinema does not claim ownership of third-party intellectual property unless explicitly stated.</p>
    </Section>

    <Section heading="7. Third-Party Websites and Services">
      <p>The website may contain links or references to third-party websites, platforms, or services.</p>
      <p>
        Absolute Cinema does not control third-party websites or services and is not responsible for
        their content, availability, privacy practices, security, or terms.
      </p>
      <p>
        Your use of third-party websites or services is subject to the applicable terms and policies
        of those third parties.
      </p>
    </Section>

    <Section heading="8. Website Availability">
      <p>We aim to keep Absolute Cinema available and functioning properly.</p>
      <p>However, the website may occasionally be unavailable due to:</p>
      <Bullets items={[
        "Maintenance",
        "Updates",
        "Technical problems",
        "Hosting issues",
        "Network problems",
        "Circumstances beyond our reasonable control",
      ]} />
      <p>We do not guarantee uninterrupted or error-free availability.</p>
    </Section>

    <Section heading="9. Disclaimer">
      <p>The website and its features are provided on an "as available" basis.</p>
      <p>
        While we make reasonable efforts to maintain the website, we do not guarantee that the
        website will always be available, completely accurate, secure, or free from errors.
      </p>
    </Section>

    <Section heading="10. Limitation of Liability">
      <p>
        To the extent permitted by applicable law, Absolute Cinema will not be responsible for
        losses or damages arising from your use of, or inability to use, the website or third-party
        websites and services linked from the website.
      </p>
      <p>
        Nothing in these Terms is intended to exclude or limit any liability that cannot legally be
        excluded or limited.
      </p>
    </Section>

    <Section heading="11. Changes to the Website">
      <p>We may update, modify, add, suspend, or remove website features or content from time to time.</p>
      <p>We may also perform maintenance or technical updates when necessary.</p>
    </Section>

    <Section heading="12. Changes to These Terms">
      <p>We may update these Terms of Service from time to time.</p>
      <p>The Last Updated date will be changed when material updates are made.</p>
      <p>
        Your continued use of the website after an update means that you accept the updated Terms, to
        the extent permitted by applicable law.
      </p>
    </Section>

    <Section heading="13. Contact Us">
      <p>If you have questions about these Terms of Service, please contact us:</p>
      <p>
        Email:{" "}
        <a href="mailto:absolutecinema2503@gmail.com" className="ac-gold-text hover:underline">
          absolutecinema2503@gmail.com
        </a>
      </p>
    </Section>
  </StaticPage>
);
